import { Injectable } from '@angular/core';
import { MonitoringMetricType, MetricSourceType, MetricAggregationFunc } from '../../enums/generated.enums';
import { ApiConversionHelper } from '../../helpers/api-conversion.helper';
import { DateHelper } from '../../helpers/date.helper';
import { AnomalySources } from '../../types/anomaly-sources.type';
import { MetricBarChartData, MultiMetricBarChartItem } from '../../types/metric-barchart-data.type';
import { SupportedMetrics } from '../../types/metric-description.type';
import { MetricFilters } from '../../types/metric-filters.type';
import { MetricData } from '../../types/metric-timeseries-data.type';
import { TopExceptionsCountTrend } from '../../types/top-exceptions-count-trend.type';
import { BaseApiService } from './base-api.service';
import { Cacheable } from '../../decorators/cache.decorator';

@Injectable({ providedIn: 'root' })
export class MetricsApiService extends BaseApiService {
  protected get baseUrl(): string {
    return `${super.baseUrl}/metrics`;
  }

  @Cacheable()
  async getSupportedMetrics(): Promise<SupportedMetrics> {
    return await this.get<SupportedMetrics>('supported');
  }

  async getAvailableMetrics(tenantId: string, from: Date, to: Date, filters?: MetricFilters): Promise<MonitoringMetricType[]> {
    return await this.get<MonitoringMetricType[]>(`available/${DateHelper.formatApiDateOnly(from)}/${DateHelper.formatApiDateOnly(to)}${this.getTenantParameter(tenantId)}`, filters);
  }

  async getMetricData(metricType: MonitoringMetricType, tenantId: string, from: Date, to: Date, filters: MetricFilters, bucketSize?: number): Promise<MetricData> {
    const result = await this.get<MetricData>(`${metricType}/${DateHelper.formatApiDateTime(from)}/${DateHelper.formatApiDateTime(to)}${this.getTenantParameter(tenantId)}`, { ...filters, bucketSize });
    ApiConversionHelper.convertDates(result.samples, ['timeStamp']);
    return result;
  }

  async getMetricPerSelectedOwners(metricType: MonitoringMetricType, tenantId: string, date: Date, ownerIds: string[]): Promise<MetricBarChartData> {
    return await this.get<MetricBarChartData>(`${metricType}/per-selected-owners/${DateHelper.formatApiDateOnly(date)}${this.getTenantParameter(tenantId)}`, { ownerIds });
  }

  async getMetricBreakdownPerOwner(metricType: MonitoringMetricType, tenantId: string, from: Date, to: Date, maxCount: number): Promise<MetricBarChartData> {
    return await this.get<MetricBarChartData>(`${metricType}/per-owner/${DateHelper.formatApiDateTime(from)}/${DateHelper.formatApiDateTime(to)}${this.getTenantParameter(tenantId)}`, { maxCount });
  }

  async getMetricBreakdownPerHour(metricType: MonitoringMetricType, tenantId: string, from: Date, to: Date, businessHoursOnly: boolean, aggregationFunc: MetricAggregationFunc, filters?: MetricFilters): Promise<MetricBarChartData> {
    return await this.get<MetricBarChartData>(`${metricType}/per-hour/${DateHelper.formatApiDateTime(from)}/${DateHelper.formatApiDateTime(to)}${this.getTenantParameter(tenantId)}`, {
      ...filters, aggregationFunc, businessHoursOnly,
    });
  }

  async getMetricBreakdownPerWeekDay(metricType: MonitoringMetricType, tenantId: string, from: Date, to: Date, aggregationFunc: MetricAggregationFunc, filters?: MetricFilters): Promise<MetricBarChartData> {
    return await this.get<MetricBarChartData>(`${metricType}/per-weekday/${DateHelper.formatApiDateTime(from)}/${DateHelper.formatApiDateTime(to)}${this.getTenantParameter(tenantId)}`, {
      ...filters, aggregationFunc,
    });
  }

  async getMetricBreakdownPerResource(metricType: MonitoringMetricType, tenantId: string, from: Date, to: Date, maxCount: number, aggregationFunc: MetricAggregationFunc, filters?: MetricFilters): Promise<MetricBarChartData> {
    return await this.get<MetricBarChartData>(`${metricType}/per-resource/${DateHelper.formatApiDateTime(from)}/${DateHelper.formatApiDateTime(to)}${this.getTenantParameter(tenantId)}`, {
      ...filters, aggregationFunc, maxCount,
    });
  }

  async getTopExceptions(tenantId: string, from: Date, to: Date, sourceType: MetricSourceType, exceptionCount: number, filters?: MetricFilters): Promise<MetricBarChartData> {
    return await this.get<MetricBarChartData>(`top-exceptions/${sourceType}/${DateHelper.formatApiDateTime(from)}/${DateHelper.formatApiDateTime(to)}${this.getTenantParameter(tenantId)}`, { ...filters, exceptionCount });
  }

  async getTopExceptionSources(tenantId: string, from: Date, to: Date, sourceType: MetricSourceType, exceptionCount: number, filters?: MetricFilters): Promise<MetricBarChartData> {
    return await this.get<MetricBarChartData>(`top-exception-sources/${sourceType}/${DateHelper.formatApiDateTime(from)}/${DateHelper.formatApiDateTime(to)}${this.getTenantParameter(tenantId)}`, { ...filters, exceptionCount });
  }

  async getTopExceptionsTrendOverDays(tenantId: string, from: Date, to: Date, exceptionCount: number, filters?: MetricFilters): Promise<TopExceptionsCountTrend> {
    return await this.get<TopExceptionsCountTrend>(`top-exceptions-trend/count-over-days/${DateHelper.formatApiDateTime(from)}/${DateHelper.formatApiDateTime(to)}${this.getTenantParameter(tenantId)}`, {
      ...filters, exceptionCount,
    });
  }

  async getTopExceptionsTrendOverHours(tenantId: string, from: Date, to: Date, exceptionCount: number, filters?: MetricFilters): Promise<TopExceptionsCountTrend> {
    return await this.get<TopExceptionsCountTrend>(`top-exceptions-trend/rate-over-hours/${DateHelper.formatApiDateTime(from)}/${DateHelper.formatApiDateTime(to)}${this.getTenantParameter(tenantId)}`, {
      ...filters, exceptionCount,
    });
  }

  async getMostIncreasingExceptions(tenantId: string, from: Date, to: Date, periodLength: number, exceptionCount: number, filters?: MetricFilters): Promise<MultiMetricBarChartItem[]> {
    return await this.get<MultiMetricBarChartItem[]>(`most-increasing-exceptions/${DateHelper.formatApiDateTime(from)}/${DateHelper.formatApiDateTime(to)}${this.getTenantParameter(tenantId)}`, {
      ...filters, periodLength, exceptionCount,
    });
  }

  async getAnomalies(tenantId: string, from: Date, to: Date, filters?: MetricFilters): Promise<MetricData> {
    const result = await this.get<MetricData>(`anomalies/${DateHelper.formatApiDateTime(from)}/${DateHelper.formatApiDateTime(to)}${this.getTenantParameter(tenantId)}`, { ...filters });
    ApiConversionHelper.convertDates(result.samples, ['timeStamp']);
    return result;
  }

  async getAnomalySources(tenantId: string, from: Date, to: Date, maxCount: number): Promise<AnomalySources> {
    return await this.get<AnomalySources>(`anomalies-sources/${DateHelper.formatApiDateTime(from)}/${DateHelper.formatApiDateTime(to)}${this.getTenantParameter(tenantId)}`, { maxCount });
  }
}
